import { useState, useEffect } from 'react';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search, ExternalLink } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { driverProfileName } from '@/lib/driverProfile';

export default function LoanAgreementsRecords() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['loan-agreements-count'],
        queryFn: async () => {
            const { count, error } = await supabase.from('loan_agreements').select('*', { count: 'exact', head: true });
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({ totalCount: countData || 0 });

    useEffect(() => { resetPage(); }, [debouncedSearch]);

    const { data: agreements, isLoading } = useQuery({
        queryKey: ['loan-agreements', currentPage],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('loan_agreements')
                // `admin_id` IS SELECTED SO THE DRIVER'S OWN ROW CAN BE PICKED OUT — see
                // `driverProfileOf`; the embed can carry an admin's test profile beside it.
                .select('*, loans(contracts(drivers(profiles(id, admin_id, first_name, last_name))))')
                .order('signed_at', { ascending: false })
                .range(from, to);
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    return (
        <Card className="border-0">
            <CardHeader>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            </CardHeader>
            <CardContent>
                <ListPagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} />
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : agreements && agreements.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Signed</TableHead>
                                <TableHead>Advance ID</TableHead>
                                <TableHead>Driver</TableHead>
                                <TableHead>Document</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {agreements.map((a) => {
                                const driver = driverProfileName(a.loans?.contracts?.drivers?.profiles) ?? '-';
                                return (
                                    <TableRow key={a.id}>
                                        <TableCell>{a.signed_at ? new Date(a.signed_at).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell>#{a.loan_id}</TableCell>
                                        <TableCell>{driver}</TableCell>
                                        <TableCell>
                                            {a.agreement_url ? (
                                                <button onClick={() => openSignedUrl(a.agreement_url!)} className="text-primary hover:underline flex items-center gap-1">
                                                    View <ExternalLink className="h-3 w-3" />
                                                </button>
                                            ) : '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">No advance agreements found</p>
                )}
            </CardContent>
        </Card>
    );
}
